<template>
  <div class="mt-1 mb-3">
    <h3 class="mb-1 text-lg font-bold">{{ $t("settings.devices") }}</h3>
    <div v-if="hasHardwareLocation" class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">Device Location:</p>
      <div class="w-1/3">{{ hardwareLocation.deviceName }}</div>
    </div>
    <div v-else class="flex h-[50px] items-center">
      <p class="w-1/3 text-base">{{ $t("settings.device") }}</p>
      <div class="w-1/3">
        <SSelect v-model="deviceId">
          <SSelectTrigger class="w-full">
            <SSelectValue :placeholder="$t('select.device')" />
          </SSelectTrigger>
          <SSelectContent>
            <SSelectItem
              v-for="dev in deviceItems"
              :key="dev.id || ''"
              :value="dev.id || ''"
            >
              <span v-if="isV2" class="flex items-center gap-2">
                <span
                  class="inline-flex items-center justify-center w-6 h-6 rounded-md"
                  :class="dev.bgClass"
                >
                  <component
                    :is="dev.icon"
                    class="w-3.5 h-3.5"
                    :class="dev.colorClass"
                  />
                </span>
                <span>{{ dev.name || "" }}</span>
              </span>
              <template v-else>{{ dev.name || "" }}</template>
            </SSelectItem>
          </SSelectContent>
        </SSelect>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import DeviceModule from "@/store/modules/device";
import { CashLocationDeviceType } from "@/constants/cash-location";
import { isCashbookV2Enabled } from "@/utils/helpers/feature-flags";
import {
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
} from "@smeetz/ui-core";
import { locationVisualFor } from "@/utils/vue-helpers/cash-location";

export default defineComponent({
  name: "DeviceLocationSettings",
  components: {
    SSelect,
    SSelectContent,
    SSelectItem,
    SSelectTrigger,
    SSelectValue,
  },
  async setup() {
    const deviceId = ref<number | null>(DeviceModule.deviceId || null);
    const hasHardwareLocation = computed(() => {
      if (DeviceModule.hardwareLocation) {
        return true;
      } else {
        return false;
      }
    });

    if (!hasHardwareLocation.value) {
      // if we get no device or we get a device without location then we pull all devices from backend
      // and in the settings popup we show a dropdown with all the devices for the user to select one of them
      // and once he submits his selection we call the api created by Saoussene and we supply the hardware id
      await DeviceModule.fetchDevices();
    }

    const hardwareLocation = computed(() => {
      return DeviceModule.hardwareLocation;
    });

    const isV2 = computed(() => isCashbookV2Enabled());

    // Pre-v2 this list showed every device, unfiltered. The POS-only
    // filter was introduced for cashbook v2's location picker and should
    // stay scoped to it — legacy (non-v2) keeps the original behavior.
    const devices = computed(() => {
      const all = DeviceModule.getDevices || [];
      if (!isV2.value) {
        return all;
      }
      return all.filter((dev: any) => {
        const dt = dev.cashLocation?.DeviceType;
        return dt == null || dt === CashLocationDeviceType.POS;
      });
    });

    const deviceItems = computed(() =>
      devices.value.map((dev: any) => ({
        ...dev,
        ...locationVisualFor(dev.cashLocation?.DeviceType),
      }))
    );

    return {
      hasHardwareLocation,
      deviceId,
      hardwareLocation,
      isV2,
      deviceItems,
    };
  },
});
</script>
